/**
 * Encryption and decryption utilities for TaskFortress
 * Uses AES-256-GCM from @noble/ciphers
 */

import { gcm } from "@noble/ciphers/aes"
import { sha256 } from "@noble/hashes/sha2"
import { sha3_512 } from "@noble/hashes/sha3"
import { bytesToHex, hexToBytes } from "@noble/hashes/utils"
import { Argon2, Argon2Mode } from "@sphereon/isomorphic-argon2"
import * as elliptic from "elliptic"
import { getRandomBytes } from "./utils"

// Initialize elliptic with secp256k1 curve
const ec = new elliptic.ec("secp256k1")

// Size constants
const NOISE_SIZE = 64 // Size of random noise to add in bytes
const GCM_IV_SIZE = 12 // Size of initialization vector for AES-GCM in bytes

/**
 * Represents an ECC key pair for authentication
 */
export type ECCKeyPair = {
  publicKey: string
  privateKey: string
}

/**
 * Result of the key derivation process
 */
export class DerivedKeys {
  authKeyPair: ECCKeyPair
  encryptionKey: Uint8Array

  constructor(authKeyPair: ECCKeyPair, encryptionKey: Uint8Array) {
    this.authKeyPair = authKeyPair
    this.encryptionKey = encryptionKey
  }

  /**
   * Serializes the DerivedKeys instance to a JSON string
   */
  toJSON(): string {
    return JSON.stringify({
      authKeyPair: {
        publicKey: this.authKeyPair.publicKey,
        privateKey: this.authKeyPair.privateKey,
      },
      encryptionKey: bytesToHex(this.encryptionKey),
    })
  }

  /**
   * Creates a DerivedKeys instance from a JSON string
   */
  static fromJSON(json: string): DerivedKeys {
    const data = JSON.parse(json)
    return new DerivedKeys(
      {
        publicKey: data.authKeyPair.publicKey,
        privateKey: data.authKeyPair.privateKey,
      },
      hexToBytes(data.encryptionKey),
    )
  }
}

/**
 * Derives an ECC key pair and encryption key from a password and master key
 * @param password User password
 * @param masterKey Master key string
 * @returns Promise resolving to derived keys (ECC key pair and encryption key)
 */
export async function deriveKeys(
  password: string,
  masterKey: string,
): Promise<DerivedKeys> {
  // Concatenate password and master key
  const combined = password + masterKey

  // Hash with SHA3-512 for key pair derivation
  const hash = sha3_512(combined)

  // Generate salt using SHA-256 of the combined input
  const salt = sha256(combined)

  // Derive ECC key pair using the hash as a deterministic seed
  const keyPair = ec.keyFromPrivate(hash)
  const publicKey = keyPair.getPublic("hex")
  const privateKey = keyPair.getPrivate("hex")

  // Derive encryption key using Argon2id with the full hash for maximum entropy
  const result = await Argon2.hash(hash, salt, {
    hashLength: 32, // 256 bits
    iterations: 10, // Time cost
    memory: 65536, // 64 MB, Memory cost
    parallelism: 4,
    mode: Argon2Mode.Argon2id,
  })

  // Convert hex to bytes for encryption key
  const encryptionKey = hexToBytes(result.hex)

  return new DerivedKeys(
    {
      publicKey,
      privateKey,
    },
    encryptionKey,
  )
}

/**
 * Signs a challenge string using ECC private key
 * @param challenge The challenge string to sign
 * @param privateKey The ECC private key for signing
 * @returns The signature as a string
 */
export async function signChallenge(
  challenge: string,
  privateKey: string,
): Promise<string> {
  // Create key from private key
  const key = ec.keyFromPrivate(privateKey)

  // Hash the challenge
  const challengeHash = sha256(challenge)

  // Sign the hash with the private key
  const signature = key.sign(challengeHash)

  // Return the signature in DER format
  return signature.toDER("hex")
}

/**
 * Verifies a challenge signature using ECC public key
 * @param challenge The original challenge string
 * @param signature The signature produced by signChallenge
 * @param publicKey The ECC public key
 * @throws Error if verification fails
 */
export async function verifySignature(
  challenge: string,
  signature: string,
  publicKey: string,
): Promise<void> {
  try {
    // Create key from public key
    const key = ec.keyFromPublic(publicKey, "hex")

    // Hash the challenge
    const challengeHash = sha256(challenge)

    // Verify the signature
    const isValid = key.verify(challengeHash, signature)

    if (!isValid) {
      throw new Error("Invalid signature")
    }
  } catch (_error) {
    // Throw a more specific error
    throw new Error("Signature verification failed")
  }
}

/**
 * Encrypts a string using AES-256-GCM with added noise
 * @param data String to encrypt
 * @param key Encryption key as Uint8Array
 * @returns Hex string containing encrypted data with IV and authentication tag
 */
export async function encrypt(data: string, key: Uint8Array): Promise<string> {
  // Generate IV for AES-GCM
  const iv = getRandomBytes(GCM_IV_SIZE)

  // Generate random noise to add to the payload
  const noise = getRandomBytes(NOISE_SIZE)

  // Convert string data to bytes
  const dataBytes = new TextEncoder().encode(data)

  // Store data length as 4 bytes (for decryption) + data + noise
  const dataLength = dataBytes.length

  // Create buffer: length (4 bytes) + data + noise
  const plaintext = new Uint8Array(4 + dataLength + NOISE_SIZE)

  // Write data length as 4 bytes (big endian)
  plaintext[0] = (dataLength >> 24) & 0xff
  plaintext[1] = (dataLength >> 16) & 0xff
  plaintext[2] = (dataLength >> 8) & 0xff
  plaintext[3] = dataLength & 0xff

  // Copy data
  plaintext.set(dataBytes, 4)

  // Add noise at the end
  plaintext.set(noise, 4 + dataLength)

  // Encrypt using AES-256-GCM
  const aesgcm = gcm(key, iv)
  const ciphertext = aesgcm.encrypt(plaintext)

  // Combine IV and ciphertext
  const result = new Uint8Array(iv.length + ciphertext.length)
  result.set(iv, 0)
  result.set(ciphertext, iv.length)

  // Return as hex string
  return bytesToHex(result)
}

/**
 * Decrypts data encrypted with the encrypt function
 * @param encryptedHex Hex string containing encrypted data
 * @param key Encryption key as Uint8Array
 * @returns The decrypted string
 */
export async function decrypt(
  encryptedHex: string,
  key: Uint8Array,
): Promise<string> {
  // Convert hex to bytes
  const encrypted = hexToBytes(encryptedHex)

  // Extract IV and ciphertext
  const iv = encrypted.subarray(0, GCM_IV_SIZE)
  const ciphertext = encrypted.subarray(GCM_IV_SIZE)

  // Decrypt using AES-256-GCM
  const aesgcm = gcm(key, iv)
  const plaintext = aesgcm.decrypt(ciphertext)

  // Extract the original data length from the first 4 bytes
  const dataLength =
    (plaintext[0] << 24) |
    (plaintext[1] << 16) |
    (plaintext[2] << 8) |
    plaintext[3]

  // Extract the original data (without noise)
  const dataBytes = plaintext.subarray(4, 4 + dataLength)

  // Convert bytes back to string
  return new TextDecoder().decode(dataBytes)
}
